/* Hoppinger Feedback, after the redesign: a dark Hoppinger-green sidebar with bright-green actions.
   Simula for display type, TT Commons for text (loaded by src/fonts.ts). Always dark, so it stands
   apart from whatever product it sits next to. */

:host {
  all: initial;
}

.hfb-root {
  /* Style guide tokens, widget (always dark) column. */
  --hfb-brand: #06312a;
  --hfb-brand-fg: #faf5f2;
  --hfb-accent: #3cc689;
  --hfb-accent-hover: #5fd3a0;
  --hfb-accent-fg: #06312a;
  --hfb-highlight: #3cc689;
  --hfb-bg: #0d1f1b;
  --hfb-subtle: #132c26;
  --hfb-fg: #faf5f2;
  --hfb-muted: #a9b7b2;
  --hfb-border: #24433c;
  --hfb-danger: #ff9278;
  --hfb-mark: #e1491b;
  --hfb-tab-active: #3cc689;
  --hfb-focus: #3cc689;
  --hfb-accent-subtle: #163a30;
  --hfb-danger-subtle: #3a2019;
  --hfb-input-bg: #0a1916;
  --hfb-skeleton: #1b3832;
  --hfb-overlay: rgba(6, 49, 42, 0.48);
  /* Derived surfaces used by the components. */
  --hfb-header: #0f2b25;
  --hfb-surface: var(--hfb-subtle);
  --hfb-surface-2: #183530;
  --hfb-surface-3: #1e3f37;
  --hfb-border-strong: #2f554c;
  --hfb-faint: #6f8781;
  --hfb-bug: var(--hfb-danger);
  --hfb-danger-bg: var(--hfb-danger-subtle);
  --hfb-danger-border: #7a3424;
  --hfb-radius: 10px;
  --hfb-radius-small: 8px;
  --hfb-shadow: 0 2px 4px rgba(0, 0, 0, 0.18), 0 12px 32px rgba(0, 0, 0, 0.32);
  --hfb-font: 'HFB TT Commons', 'HFB TT Commons fallback', system-ui, sans-serif;
  --hfb-font-display: 'HFB Simula', 'HFB Simula fallback', Georgia, serif;
  --hfb-font-mono: ui-monospace, SFMono-Regular, Menlo, monospace;

  font: 15px/1.45 var(--hfb-font);
  color: var(--hfb-fg);
  -webkit-font-smoothing: antialiased;
  color-scheme: dark;
}

.hfb-root *,
.hfb-root *::before,
.hfb-root *::after {
  box-sizing: border-box;
}

/* :where() keeps this reset at zero specificity, so component classes always win. */
:where(.hfb-root) button,
:where(.hfb-root) input,
:where(.hfb-root) textarea {
  font: inherit;
  color: inherit;
}
:where(.hfb-root) button {
  cursor: pointer;
}
:where(.hfb-root) p {
  margin: 0;
}

.hfb-icon {
  flex: none;
  display: block;
}
.hfb-spinner,
.hfb-spin {
  animation: hfb-rotate 0.9s linear infinite;
}
@keyframes hfb-rotate {
  to {
    transform: rotate(360deg);
  }
}
@media (prefers-reduced-motion: reduce) {
  .hfb-spinner,
  .hfb-spin {
    animation-duration: 3s;
  }
}

.hfb-sr {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
}

:where(.hfb-root) :focus-visible {
  outline: 2px solid var(--hfb-focus);
  outline-offset: 2px;
}

/* ---------- Launcher: 36 × 116 tab on the right edge */
.hfb-launcher {
  position: fixed;
  top: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  width: 36px;
  height: 116px;
  padding: 12px 0;
  border: 0;
  background: var(--hfb-brand);
  color: #faf5f2;
  box-shadow: var(--hfb-shadow);
  transition: transform 140ms ease;
}
.hfb-launcher-text {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 0.02em;
}
.hfb-side-right .hfb-launcher {
  right: 0;
  border-radius: 10px 0 0 10px;
  transform: translateY(-50%);
}
.hfb-side-right .hfb-launcher:hover {
  transform: translate(-6px, -50%);
}
.hfb-side-left .hfb-launcher {
  left: 0;
  border-radius: 0 10px 10px 0;
  transform: translateY(-50%);
}
.hfb-side-left .hfb-launcher:hover {
  transform: translate(6px, -50%);
}
.hfb-launcher:hover .hfb-icon {
  color: var(--hfb-accent);
}
.hfb-launcher:focus-visible {
  outline: none;
  box-shadow:
    0 0 0 2px #ffffff,
    0 0 0 4px var(--hfb-accent),
    var(--hfb-shadow);
}
.hfb-launcher-dot {
  position: absolute;
  top: -5px;
  left: -5px;
  width: 13px;
  height: 13px;
  border-radius: 50%;
  background: var(--hfb-accent);
  border: 2px solid #ffffff;
}
@media (prefers-reduced-motion: reduce) {
  .hfb-launcher {
    transition: none;
  }
}

/* ---------- Panel */
.hfb-panel {
  position: fixed;
  top: 0;
  bottom: 0;
  width: 100vw;
  display: flex;
  flex-direction: column;
  background: var(--hfb-bg);
  cursor: auto;
}
.hfb-side-right .hfb-panel {
  right: 0;
  border-left: 1px solid #000;
}
.hfb-side-left .hfb-panel {
  left: 0;
  border-right: 1px solid #000;
}
.hfb-panel:not(.is-docked) {
  box-shadow: var(--hfb-shadow);
}
/* Docked: a soft shadow cast onto the product, so the sidebar reads as separate from it. */
.hfb-side-right .hfb-panel.is-docked {
  box-shadow:
    -1px 0 2px rgba(6, 49, 42, 0.08),
    -6px 0 16px rgba(6, 49, 42, 0.12),
    -16px 0 40px rgba(6, 49, 42, 0.14);
}
.hfb-side-left .hfb-panel.is-docked {
  box-shadow:
    1px 0 2px rgba(6, 49, 42, 0.08),
    6px 0 16px rgba(6, 49, 42, 0.12),
    16px 0 40px rgba(6, 49, 42, 0.14);
}

.hfb-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 14px 16px 20px;
  background: var(--hfb-header);
}
.hfb-brand {
  display: flex;
  align-items: center;
  gap: 12px;
}
.hfb-logo {
  display: block;
  color: #faf5f2;
}
.hfb-brand-sep {
  width: 1px;
  height: 22px;
  background: rgba(250, 245, 242, 0.3);
}
.hfb-brand-title {
  font: 400 22px/1 var(--hfb-font-display);
  letter-spacing: -0.01em;
  color: #faf5f2;
}
.hfb-icon-btn {
  display: inline-grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 50%;
  background: none;
  color: inherit;
}
.hfb-icon-btn:hover {
  background: rgba(250, 245, 242, 0.1);
}

/* Tabs */
.hfb-tabs {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 0 20px;
  background: var(--hfb-header);
  border-bottom: 1px solid var(--hfb-border);
}
.hfb-tabs > button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 0 11px;
  border: 0;
  border-bottom: 3px solid transparent;
  background: none;
  color: var(--hfb-muted);
  font-weight: 500;
}
.hfb-tabs > button.is-active {
  color: var(--hfb-fg);
  font-weight: 700;
  border-bottom-color: var(--hfb-tab-active);
}
.hfb-count {
  display: inline-grid;
  place-items: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: var(--hfb-accent);
  color: var(--hfb-accent-fg);
  font-size: 11px;
  font-weight: 800;
}

/* Pins switch */
.hfb-switch {
  margin-left: auto;
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--hfb-muted);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}
.hfb-switch input {
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  cursor: pointer;
}
.hfb-switch-track {
  position: relative;
  width: 32px;
  height: 18px;
  margin-left: 4px;
  border-radius: 999px;
  background: var(--hfb-surface-3);
  transition: background 120ms;
}
.hfb-switch-track::after {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #faf5f2;
  transition: transform 120ms;
}
.hfb-switch input:checked ~ .hfb-switch-track {
  background: var(--hfb-accent);
}
.hfb-switch input:checked ~ .hfb-switch-track::after {
  transform: translateX(14px);
}
.hfb-switch input:focus-visible ~ .hfb-switch-track {
  outline: 2px solid var(--hfb-accent);
  outline-offset: 2px;
}

/* Body */
.hfb-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 18px 20px 0;
  overflow-y: auto;
  scrollbar-color: var(--hfb-border-strong) transparent;
}
.hfb-body > :not(.hfb-form-new):not(.hfb-thread) {
  padding-bottom: 20px;
}
.hfb-body.is-thread {
  padding-top: 14px;
}

/* Footer */
.hfb-footer {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  border-top: 1px solid var(--hfb-border);
  background: var(--hfb-header);
  font-size: 14px;
}
.hfb-footer-who {
  flex: 1;
  min-width: 0;
  display: flex;
  gap: 8px;
  align-items: baseline;
  overflow: hidden;
  white-space: nowrap;
}
.hfb-footer-who span {
  color: var(--hfb-muted);
  overflow: hidden;
  text-overflow: ellipsis;
}
.hfb-avatar {
  flex: none;
  display: inline-grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--hfb-surface-3);
  color: var(--hfb-fg);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.02em;
}

/* ---------- Text and generic pieces */
.hfb-display {
  margin: 0;
  font: 400 30px/1.12 var(--hfb-font-display);
  letter-spacing: -0.015em;
  text-wrap: balance;
}
.hfb-muted {
  color: var(--hfb-muted);
}
.hfb-hint {
  color: var(--hfb-muted);
  font-size: 13px;
  line-height: 1.4;
}
.hfb-hint.is-strong {
  color: var(--hfb-fg);
}
.hfb-link {
  border: 0;
  padding: 0;
  background: none;
  color: var(--hfb-accent);
  font-weight: 700;
}
.hfb-link:hover {
  text-decoration: underline;
  text-underline-offset: 3px;
}
.hfb-link-quiet {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 0;
  padding: 0;
  background: none;
  color: var(--hfb-fg);
  font-weight: 600;
  font-size: 14px;
}
.hfb-link-quiet:disabled {
  opacity: 0.6;
}
kbd {
  display: inline-block;
  min-width: 22px;
  padding: 1px 6px;
  border: 1px solid var(--hfb-border-strong);
  border-bottom-width: 2px;
  border-radius: 5px;
  font: 600 12px/1.4 var(--hfb-font);
  text-align: center;
  color: var(--hfb-fg);
}

.hfb-card {
  border: 1px solid var(--hfb-border);
  border-radius: var(--hfb-radius);
  background: var(--hfb-surface);
}

/* Buttons: pills */
.hfb-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 11px 22px;
  border: 1px solid transparent;
  border-radius: 999px;
  font-weight: 700;
  line-height: 1.1;
  transition:
    background-color 120ms,
    border-color 120ms;
}
.hfb-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.hfb-btn-primary {
  background: var(--hfb-accent);
  color: var(--hfb-accent-fg);
}
.hfb-btn-primary:hover:not(:disabled) {
  background: var(--hfb-accent-hover);
}
.hfb-btn-outline {
  background: none;
  border-color: rgba(238, 243, 241, 0.75);
  color: var(--hfb-fg);
}
.hfb-btn-outline:hover:not(:disabled) {
  border-color: var(--hfb-accent);
  color: var(--hfb-accent);
}
.hfb-btn-ghost {
  background: none;
  color: var(--hfb-muted);
}
.hfb-btn-block {
  width: 100%;
}
.hfb-btn-large {
  padding: 15px 22px;
  font-size: 17px;
}
.hfb-btn-small {
  padding: 6px 14px;
  font-size: 13px;
}
.hfb-actions {
  display: flex;
  gap: 10px;
}

/* Forms */
.hfb-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.hfb-form-new {
  flex: 1 0 auto;
}
.hfb-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.hfb-label {
  font-weight: 700;
  font-size: 15px;
}
.hfb-label-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}
.hfb-saved {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.hfb-input {
  width: 100%;
  padding: 11px 14px;
  border: 1px solid var(--hfb-border);
  border-radius: var(--hfb-radius);
  background: var(--hfb-input-bg);
  color: var(--hfb-fg);
  resize: vertical;
}
.hfb-input::placeholder {
  color: var(--hfb-faint);
}
.hfb-input:focus {
  outline: none;
  border-color: var(--hfb-accent);
  box-shadow: 0 0 0 1px var(--hfb-accent);
}
.hfb-input.is-invalid {
  border-color: var(--hfb-danger);
  box-shadow: 0 0 0 1px var(--hfb-danger);
}
.hfb-field-error {
  display: flex;
  gap: 6px;
  align-items: flex-start;
  color: var(--hfb-danger);
  font-size: 14px;
  font-weight: 600;
}
.hfb-field-error .hfb-icon {
  margin-top: 2px;
}

/* Segmented: bug / feature */
.hfb-segmented {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
  padding: 4px;
  border: 1px solid var(--hfb-border);
  border-radius: 999px;
  background: var(--hfb-surface);
}
.hfb-segmented button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 8px;
  border: 0;
  border-radius: 999px;
  background: none;
  color: var(--hfb-fg);
  font-weight: 700;
  white-space: nowrap;
}
.hfb-segmented button.is-active {
  background: var(--hfb-accent);
  color: var(--hfb-accent-fg);
}

/* ---------- Story editor */
.hfb-story {
  position: relative;
  border: 1px solid var(--hfb-border);
  border-radius: var(--hfb-radius);
  background: var(--hfb-input-bg);
}
.hfb-story-toolbar,
.hfb-story-tools {
  background: var(--hfb-subtle);
}
.hfb-story:focus-within {
  border-color: var(--hfb-accent);
  box-shadow: 0 0 0 1px var(--hfb-accent);
}
.hfb-story-toolbar {
  display: flex;
  gap: 2px;
  padding: 6px 8px;
  border-bottom: 1px solid var(--hfb-border);
}
.hfb-tool {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  border: 1px solid transparent;
  border-radius: var(--hfb-radius-small);
  background: none;
  color: var(--hfb-fg);
  font-size: 15px;
}
.hfb-tool:hover:not(:disabled) {
  background: var(--hfb-surface-2);
}
.hfb-tool.is-active,
.hfb-tool.is-active:hover:not(:disabled) {
  background: var(--hfb-accent);
  color: var(--hfb-accent-fg);
}
.hfb-tool:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.hfb-linkbar {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 8px;
  border-bottom: 1px solid var(--hfb-border);
}
.hfb-linkbar .hfb-input {
  flex: 1;
  min-width: 0;
  padding: 7px 10px;
  border-radius: var(--hfb-radius-small);
  border-color: var(--hfb-accent);
}
.hfb-story-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 14px 12px;
  min-height: 150px;
}
.hfb-story-body > * {
  flex-shrink: 0;
}
.hfb-story-text {
  min-height: 24px;
  outline: none;
  line-height: 1.55;
  overflow-wrap: anywhere;
}
.hfb-story-text[data-empty='true']::before {
  content: attr(data-placeholder);
  color: var(--hfb-faint);
  pointer-events: none;
}
.hfb-story-text a {
  color: var(--hfb-accent);
  background: rgba(60, 198, 137, 0.14);
  border-radius: 3px;
  text-decoration: none;
}
.hfb-story-text b,
.hfb-story-text strong {
  font-weight: 800;
}

.hfb-capture {
  margin: 0;
  border: 1px solid var(--hfb-border);
  border-radius: var(--hfb-radius-small);
  overflow: hidden;
  user-select: none;
  background: var(--hfb-surface-2);
}
.hfb-capture img {
  display: block;
  width: 100%;
  max-height: 150px;
  object-fit: cover;
  object-position: top center;
  background: #fff;
}
.hfb-capture-missing {
  padding: 18px;
  text-align: center;
  color: var(--hfb-muted);
  font-size: 13px;
}
.hfb-capture figcaption {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  font-size: 13px;
  color: var(--hfb-muted);
}
.hfb-capture-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.hfb-capture-label b {
  color: var(--hfb-fg);
  font-weight: 600;
}
.hfb-capture-remove {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 0;
  padding: 0;
  background: none;
  color: var(--hfb-fg);
  font-weight: 600;
}
.hfb-capture-remove:hover {
  color: var(--hfb-danger);
}

/* Where the next capture lands */
.hfb-pending {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  border: 1.5px dashed var(--hfb-accent);
  border-radius: var(--hfb-radius-small);
  background: var(--hfb-accent-subtle);
  font-size: 13px;
  line-height: 1.35;
}
.hfb-pending > .hfb-icon {
  color: var(--hfb-accent);
}
.hfb-pending > span {
  flex: 1;
}
.hfb-pending strong {
  color: var(--hfb-fg);
}
.hfb-pending.is-capturing {
  flex-direction: column;
  align-items: stretch;
  gap: 0;
  padding: 0;
  border: 1px solid var(--hfb-border);
  background: var(--hfb-surface-2);
  overflow: hidden;
}
.hfb-pending-shot {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: 108px;
  background: var(--hfb-surface-3);
  color: var(--hfb-fg);
}
.hfb-pending-caption {
  display: block;
  height: 10px;
  width: 50%;
  margin: 14px 12px;
  border-radius: 999px;
  background: var(--hfb-surface-3);
}

.hfb-story-tools {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px;
  border-top: 1px solid var(--hfb-border);
}
.hfb-tool-capture {
  height: 34px;
  padding: 0 10px;
  border-color: var(--hfb-border-strong);
  font-size: 14px;
  font-weight: 700;
}
.hfb-counter {
  margin-left: auto;
  padding-left: 4px;
  white-space: nowrap;
  color: var(--hfb-muted);
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}
.hfb-counter.is-full {
  color: var(--hfb-fg);
  font-weight: 800;
}
.hfb-dropzone {
  position: absolute;
  inset: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 2px dashed var(--hfb-accent);
  border-radius: var(--hfb-radius-small);
  background: rgba(12, 27, 24, 0.92);
  text-align: center;
  pointer-events: none;
}
.hfb-dropzone .hfb-icon {
  color: var(--hfb-accent);
  margin-bottom: 4px;
}
.hfb-dropzone span {
  color: var(--hfb-muted);
  font-size: 13px;
}

/* Technical details */
.hfb-tech-toggle {
  display: flex;
  gap: 10px;
  width: 100%;
  padding: 14px 16px;
  border: 0;
  background: none;
  text-align: left;
}
.hfb-tech-toggle > span {
  display: grid;
  gap: 2px;
}
.hfb-tech-toggle .hfb-muted {
  font-size: 14px;
}
.hfb-chevron {
  margin-top: 3px;
  color: var(--hfb-muted);
  transition: transform 120ms;
}
.hfb-tech.is-open .hfb-chevron,
details[open] > summary .hfb-chevron {
  transform: rotate(90deg);
}
.hfb-pre {
  margin: 0 16px 16px;
  padding: 10px;
  max-height: 220px;
  overflow: auto;
  border-radius: var(--hfb-radius-small);
  background: var(--hfb-bg);
  color: var(--hfb-muted);
  font: 11px/1.45 var(--hfb-font-mono);
}

/* Send: pinned to the bottom of the sidebar */
.hfb-submit-bar {
  position: sticky;
  bottom: 0;
  display: grid;
  gap: 10px;
  margin: auto -20px 0;
  padding: 14px 20px 16px;
  background: linear-gradient(to bottom, rgba(12, 27, 24, 0), var(--hfb-bg) 14px);
}
.hfb-alert {
  display: flex;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid var(--hfb-danger-border);
  border-radius: var(--hfb-radius-small);
  background: var(--hfb-danger-bg);
  font-size: 14px;
}
.hfb-alert .hfb-icon {
  margin-top: 2px;
  color: var(--hfb-danger);
}

/* ---------- Identity */
.hfb-identity {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 24px;
}
.hfb-identity .hfb-form {
  gap: 14px;
}
.hfb-identity .hfb-display {
  margin-top: 6px;
}
.hfb-privacy {
  display: flex;
  gap: 10px;
  margin: 0 -20px;
  padding: 16px 20px 18px;
  border-top: 1px solid var(--hfb-border);
  color: var(--hfb-muted);
  font-size: 13px;
}
.hfb-privacy a {
  color: var(--hfb-accent);
}

/* ---------- Sent */
.hfb-sent {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 14px;
  padding-top: 8px;
}
.hfb-sent-check {
  display: grid;
  place-items: center;
  width: 58px;
  height: 58px;
  border-radius: 50%;
  background: var(--hfb-surface-3);
  color: var(--hfb-accent);
}
.hfb-sent-card {
  align-self: stretch;
  display: grid;
  gap: 6px;
  padding: 14px 16px;
}
.hfb-sent-card header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.hfb-sent-card .hfb-muted {
  font-size: 13px;
}

/* Type and status */
.hfb-type {
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}
.hfb-type-bug {
  color: var(--hfb-bug);
}
.hfb-type-feature {
  color: var(--hfb-accent);
}
.hfb-badge {
  flex: none;
  display: inline-block;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
}
.hfb-badge-new {
  background: #213b35;
  color: #c8d9d3;
}
.hfb-badge-busy {
  background: #113f45;
  color: #7fd8de;
}
.hfb-badge-done {
  background: #2c4a1d;
  color: #c6ea9a;
}
.hfb-badge-muted {
  background: #2a3330;
  color: #b4c0bb;
}

/* ---------- Empty, loading, error */
.hfb-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 36px 12px;
  text-align: center;
}
.hfb-empty strong {
  font-size: 16px;
}
.hfb-empty .hfb-btn {
  margin-top: 8px;
}
.hfb-empty-icon {
  display: grid;
  place-items: center;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: var(--hfb-surface-3);
  color: var(--hfb-fg);
  margin-bottom: 4px;
}
.hfb-empty-icon.is-danger {
  background: #5a2419;
  color: var(--hfb-danger);
}
.hfb-skeleton {
  border: 1px solid var(--hfb-border);
  border-radius: var(--hfb-radius);
  overflow: hidden;
}
.hfb-skeleton span {
  display: block;
  height: 10px;
  border-radius: 999px;
  background: var(--hfb-skeleton);
  animation: hfb-pulse 1.4s ease-in-out infinite;
}
.hfb-skeleton-head {
  display: flex;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid var(--hfb-border);
}
.hfb-skeleton-head span {
  width: 25%;
}
.hfb-skeleton-row {
  display: grid;
  gap: 10px;
  padding: 18px 16px;
  border-bottom: 1px solid var(--hfb-border);
}
.hfb-skeleton-row span:first-child {
  width: 22%;
  height: 8px;
}
@keyframes hfb-pulse {
  50% {
    opacity: 0.5;
  }
}

/* ---------- My feedback */
.hfb-list-wrap {
  margin: -18px -20px 0;
}
.hfb-list-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
  border-bottom: 1px solid var(--hfb-border);
  font-size: 14px;
}
.hfb-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.hfb-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  padding: 16px 20px;
  border: 0;
  border-bottom: 1px solid var(--hfb-border);
  background: none;
  text-align: left;
}
.hfb-row:hover {
  background: var(--hfb-surface);
}
.hfb-row.is-unread {
  background: var(--hfb-accent-subtle);
}
.hfb-row-main {
  flex: 1;
  min-width: 0;
  display: grid;
  gap: 4px;
}
.hfb-row-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}
.hfb-row-title {
  font-weight: 700;
  font-size: 16px;
  line-height: 1.3;
}
.hfb-row .hfb-badge {
  margin-top: 2px;
}
.hfb-row-chevron {
  margin-top: 4px;
  color: var(--hfb-muted);
}
.hfb-unread {
  justify-self: start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  padding: 3px 10px;
  border-radius: 999px;
  background: var(--hfb-surface-3);
  font-size: 12px;
  font-weight: 700;
}
.hfb-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--hfb-accent);
}

/* ---------- Conversation */
.hfb-thread {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.hfb-thread-scroll {
  display: grid;
  gap: 14px;
  padding-bottom: 16px;
}
.hfb-back {
  justify-self: start;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 0;
  padding: 0;
  background: none;
  color: var(--hfb-accent);
  font-weight: 700;
  font-size: 14px;
}
.hfb-thread-head {
  display: grid;
  gap: 6px;
}
.hfb-thread-head .hfb-row-meta .hfb-badge {
  margin-left: auto;
}
.hfb-thread-head .hfb-display {
  font-size: 26px;
}
.hfb-thread-story > summary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  font-weight: 700;
  cursor: pointer;
  list-style: none;
}
.hfb-thread-story > summary::-webkit-details-marker {
  display: none;
}
.hfb-thread-story > summary .hfb-chevron {
  margin-top: 0;
}
.hfb-thread-story-body {
  display: grid;
  gap: 8px;
  padding: 0 14px 14px 38px;
  font-size: 14px;
}
.hfb-thread-story-body a {
  color: var(--hfb-accent);
}
.hfb-chip {
  justify-self: start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border: 1px solid var(--hfb-border);
  border-radius: 6px;
  color: var(--hfb-muted);
  font-size: 13px;
}
.hfb-messages {
  display: grid;
  gap: 14px;
  margin: 0 -20px;
  padding: 16px 20px 0;
  border-top: 1px solid var(--hfb-border);
}
.hfb-message {
  display: grid;
  gap: 4px;
  max-width: 88%;
}
.hfb-message header {
  font-size: 13px;
}
.hfb-message header span {
  color: var(--hfb-muted);
}
.hfb-message.is-team {
  justify-self: start;
}
.hfb-message.is-team header {
  padding-left: 38px;
}
.hfb-message.is-mine {
  justify-self: end;
  text-align: right;
}
.hfb-message-row {
  display: flex;
  align-items: flex-end;
  gap: 10px;
}
.hfb-bubble {
  padding: 10px 14px;
  border-radius: 14px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  text-align: left;
}
.hfb-message.is-team .hfb-bubble {
  background: var(--hfb-surface-2);
  border: 1px solid var(--hfb-border);
  border-bottom-left-radius: 4px;
}
.hfb-message.is-mine .hfb-bubble {
  background: var(--hfb-accent-subtle);
  border-bottom-right-radius: 4px;
}
.hfb-new-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--hfb-accent);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.hfb-new-divider::before,
.hfb-new-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--hfb-accent);
  opacity: 0.7;
}
.hfb-reply {
  position: sticky;
  bottom: 0;
  display: grid;
  gap: 8px;
  margin: auto -20px 0;
  padding: 14px 20px 16px;
  border-top: 1px solid var(--hfb-border);
  background: var(--hfb-bg);
}
.hfb-reply-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* ---------- Picking overlays */
.hfb-layer {
  position: fixed;
  inset: 0;
  user-select: none;
  -webkit-user-select: none;
}
.hfb-layer-passive {
  pointer-events: none;
}
.hfb-highlight {
  position: fixed;
  border: 2px solid var(--hfb-mark);
  border-radius: 6px;
  background: rgba(225, 73, 27, 0.08);
  transition: all 60ms linear;
}
.hfb-tag {
  position: absolute;
  bottom: calc(100% + 6px);
  right: -2px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 340px;
  padding: 4px 10px;
  border-radius: 6px;
  background: var(--hfb-brand);
  color: #faf5f2;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.hfb-tag.is-below {
  top: calc(100% + 6px);
  bottom: auto;
}
.hfb-tag code {
  font: 12px var(--hfb-font-mono);
  color: #c8d9d3;
}
.hfb-tag-dot {
  width: 7px;
  height: 7px;
  border-radius: 2px;
  background: var(--hfb-mark);
}
.hfb-area-layer {
  cursor: crosshair;
  touch-action: none;
}
.hfb-area-dim {
  position: absolute;
  inset: 0;
  background: var(--hfb-overlay);
}
.hfb-area-rect {
  position: absolute;
  border: 2px solid var(--hfb-mark);
  border-radius: 3px;
  box-shadow: 0 0 0 9999px var(--hfb-overlay);
}
.hfb-size-tag {
  position: absolute;
  padding: 3px 8px;
  border-radius: 5px;
  background: var(--hfb-brand);
  color: #faf5f2;
  font: 12px var(--hfb-font-mono);
  pointer-events: none;
}
.hfb-banner {
  position: absolute;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: calc(100% - 32px);
  padding: 7px 7px 7px 16px;
  border-radius: 999px;
  background: var(--hfb-brand);
  color: #faf5f2;
  box-shadow: var(--hfb-shadow);
  pointer-events: auto;
  cursor: default;
  white-space: nowrap;
}
.hfb-banner-icon {
  color: var(--hfb-accent);
}
.hfb-banner-text {
  display: grid;
}
.hfb-banner-text strong {
  font-weight: 700;
}
.hfb-banner-text span {
  color: rgba(250, 245, 242, 0.75);
  font-size: 13px;
}
.hfb-banner-hint {
  color: rgba(250, 245, 242, 0.75);
  font-size: 13px;
}
.hfb-banner-hint kbd {
  border-color: rgba(250, 245, 242, 0.4);
  color: #faf5f2;
}
.hfb-banner .hfb-banner-cancel {
  border-color: rgba(250, 245, 242, 0.75);
  color: #faf5f2;
}
/* Touch: a bottom bar instead of a floating banner */
.hfb-banner.is-sheet {
  top: auto;
  bottom: 0;
  left: 0;
  right: 0;
  transform: none;
  max-width: none;
  padding: 18px 20px calc(18px + env(safe-area-inset-bottom, 0px));
  border-radius: 0;
  white-space: normal;
}
.hfb-banner.is-sheet .hfb-banner-text {
  flex: 1;
}

/* ---------- Pins */
.hfb-pins {
  position: fixed;
  top: 0;
  bottom: 0;
  pointer-events: none;
}
.hfb-pin-lines {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
}
.hfb-pin-lines line {
  stroke: #06312a;
  stroke-width: 1.5;
}
.hfb-pin-lines circle {
  fill: #ffffff;
  stroke: #06312a;
  stroke-width: 1.5;
}
.hfb-pin-wrap {
  position: absolute;
  pointer-events: auto;
}
.hfb-pin {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid #ffffff;
  font: 800 13px/1 var(--hfb-font);
  box-shadow: 0 2px 8px rgba(6, 49, 42, 0.35);
}
.hfb-pin.is-mine {
  background: var(--hfb-accent);
  color: var(--hfb-accent-fg);
}
.hfb-pin.is-other {
  background: var(--hfb-brand);
  color: #faf5f2;
}
.hfb-pin.is-closed {
  background: #5d6a66;
  color: #f0f4f2;
}
.hfb-pin.is-open {
  box-shadow:
    0 0 0 3px var(--hfb-accent),
    0 2px 8px rgba(6, 49, 42, 0.35);
}
.hfb-pin-card {
  position: absolute;
  top: 36px;
  left: -8px;
  width: 300px;
  display: grid;
  gap: 4px;
  padding: 14px 16px 0;
  border-radius: var(--hfb-radius);
  background: #0f2b25;
  color: var(--hfb-fg);
  box-shadow: var(--hfb-shadow);
  font-size: 14px;
}
.hfb-pin-card.is-left {
  left: auto;
  right: -8px;
}
.hfb-pin-card.is-up {
  top: auto;
  bottom: 36px;
}
.hfb-pin-card header {
  display: flex;
  align-items: center;
  gap: 8px;
}
.hfb-pin-card header .hfb-icon-btn {
  margin-left: auto;
  width: 24px;
  height: 24px;
}
.hfb-pin-card strong {
  font-size: 16px;
  line-height: 1.3;
}
.hfb-pin-card footer {
  margin: 10px -16px 0;
  padding: 12px 16px 14px;
  border-top: 1px solid var(--hfb-border);
}
.hfb-pin-legend {
  position: absolute;
  left: 32px;
  bottom: 24px;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 9px 16px;
  border-radius: 999px;
  background: var(--hfb-brand);
  color: #faf5f2;
  font-size: 14px;
  box-shadow: var(--hfb-shadow);
  pointer-events: auto;
}
.hfb-pin-legend span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: rgba(250, 245, 242, 0.85);
}
.hfb-pin-legend i {
  width: 11px;
  height: 11px;
  border-radius: 50%;
}
.hfb-pin-legend i.is-mine {
  background: var(--hfb-accent);
}
.hfb-pin-legend i.is-other {
  border: 1.5px solid #faf5f2;
}
.hfb-pin-legend i.is-closed {
  background: #8b9490;
}

/* ---------- Component states from the style guide */

/* Primary: hover lighter, focus double ring, pressed a step darker, disabled muted. */
.hfb-btn-primary:active:not(:disabled) {
  background: #2fb079;
}
.hfb-btn:focus-visible {
  outline: none;
  box-shadow:
    0 0 0 2px var(--hfb-bg),
    0 0 0 4px var(--hfb-focus);
}
.hfb-btn-primary:disabled {
  opacity: 1;
  background: var(--hfb-surface-3);
  color: var(--hfb-faint);
}
.hfb-btn-outline:hover:not(:disabled) {
  border-color: var(--hfb-fg);
  color: var(--hfb-fg);
  background: rgba(250, 245, 242, 0.06);
}
.hfb-btn-outline:active:not(:disabled) {
  background: rgba(250, 245, 242, 0.14);
}
.hfb-btn-outline:disabled {
  opacity: 1;
  border-color: var(--hfb-border);
  color: var(--hfb-faint);
}

/* Tool buttons */
.hfb-tool:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--hfb-focus);
}
.hfb-tool:disabled {
  opacity: 1;
  color: var(--hfb-faint);
  border-color: var(--hfb-border);
  background: var(--hfb-subtle);
}

/* Text buttons: underline and a soft fill on hover */
.hfb-link {
  padding: 2px 6px;
  margin: -2px -6px;
  border-radius: 5px;
}
.hfb-link:hover {
  background: var(--hfb-accent-subtle);
}
.hfb-link:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--hfb-focus);
}
.hfb-link:disabled {
  color: var(--hfb-faint);
  background: none;
  text-decoration: none;
}

/* Text fields */
.hfb-input:hover:not(:disabled):not(:focus) {
  border-color: var(--hfb-border-strong);
}
.hfb-input:disabled {
  background: var(--hfb-subtle);
  color: var(--hfb-faint);
  border-color: var(--hfb-border);
}

/* Segmented control: keyboard focus ring on the active segment, hover on the other */
.hfb-segmented button:hover:not(.is-active) {
  background: var(--hfb-surface-2);
}
.hfb-segmented button:focus-visible {
  outline: none;
  box-shadow:
    0 0 0 2px var(--hfb-bg),
    0 0 0 4px var(--hfb-focus);
}

/* Tabs */
.hfb-tabs > button:focus-visible {
  outline: 2px solid var(--hfb-focus);
  outline-offset: 2px;
  border-radius: 3px;
}

/* Screenshot card */
.hfb-capture-view {
  position: relative;
  display: block;
  width: 100%;
  padding: 0;
  border: 0;
  background: none;
  cursor: zoom-in;
}
.hfb-capture-view::after {
  content: attr(data-hint);
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  background: rgba(13, 31, 27, 0.55);
  color: var(--hfb-fg);
  font-weight: 700;
  font-size: 13px;
  opacity: 0;
  transition: opacity 120ms;
}
.hfb-capture-view:hover::after {
  opacity: 1;
}
.hfb-capture:focus-within {
  border-color: var(--hfb-focus);
  box-shadow: 0 0 0 1px var(--hfb-focus);
}
.hfb-capture-view:focus-visible {
  outline: none;
}
.hfb-capture.is-failed {
  border-color: var(--hfb-danger-border);
}
.hfb-capture-failed {
  display: grid;
  place-items: center;
  min-height: 90px;
  padding: 16px;
  background: var(--hfb-danger-subtle);
  color: var(--hfb-danger);
  text-align: center;
  font-size: 13px;
  font-weight: 600;
}
.hfb-capture-retry {
  border: 0;
  padding: 0;
  background: none;
  color: var(--hfb-accent);
  font-weight: 700;
}

/* Full-size view of a screenshot */
.hfb-lightbox {
  position: fixed;
  inset: 0;
  z-index: 2;
  display: grid;
  place-items: center;
  padding: 32px;
  background: rgba(6, 25, 21, 0.86);
  cursor: zoom-out;
}
.hfb-lightbox img {
  max-width: 100%;
  max-height: 100%;
  border-radius: 6px;
  box-shadow: var(--hfb-shadow);
  background: #fff;
}
.hfb-lightbox .hfb-icon-btn {
  position: absolute;
  top: 16px;
  right: 16px;
  background: var(--hfb-brand);
  color: var(--hfb-brand-fg);
}

/* Pins: double ring on focus, so it shows on any product background */
.hfb-pin:focus-visible {
  outline: none;
  box-shadow:
    0 0 0 2px #ffffff,
    0 0 0 4px var(--hfb-focus);
}
.hfb-pin-card footer .hfb-btn {
  padding: 8px 16px;
  font-size: 14px;
}

/* Identities remembered in this browser */
.hfb-people {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 8px;
}
.hfb-people li {
  position: relative;
}
.hfb-person {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 44px 12px 14px;
  border: 1px solid var(--hfb-border);
  border-radius: var(--hfb-radius);
  background: var(--hfb-input-bg);
  text-align: left;
}
.hfb-person:hover {
  border-color: var(--hfb-accent);
  background: var(--hfb-accent-subtle);
}
.hfb-person:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--hfb-focus);
}
.hfb-person-text {
  flex: 1;
  min-width: 0;
  display: grid;
  line-height: 1.3;
}
.hfb-person-text .hfb-muted {
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.hfb-person .hfb-row-chevron {
  margin-top: 0;
}
.hfb-person-forget {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  color: var(--hfb-muted);
  opacity: 0;
}
.hfb-people li:hover .hfb-person-forget,
.hfb-person-forget:focus-visible {
  opacity: 1;
}
.hfb-person-forget:hover {
  color: var(--hfb-danger);
}
